import type { Step } from 'shepherd.js'
import { INBOX_SOURCES } from '@/routes/_authed/_user/inbox/-constants/inbox-sources'
import type { TourDefinition } from '../registry'
import { back, finish, gotIt, next } from '../utils/buttons'
import { attachInteractiveStep } from '../utils/interactive-step'
import { popoverOffset } from '../utils/popover-offset'
import { acquireTourLock, isTourLocked, releaseTourLock } from '../utils/tour-lock'

/**
 * Lock key that lets this tour ask SourceFilterPill to stay open while it
 * teaches what each source means. The page reads `isTourLocked('source-filter')`
 * from its handleOpenChange; this tour owns the lock for the lifetime of
 * the source-types step. Other tours could acquire the same lock if they
 * ever needed the same behaviour — the contract is named after the
 * CAPABILITY, not this tour.
 */
const SOURCE_FILTER_LOCK = 'source-filter'

/**
 * Tour-only descriptions keyed by the STABLE inbox-source enum value (not the
 * human-readable label, which can drift). The `satisfies` constraint forces
 * a TS error here if `INBOX_SOURCES` ever gains a new key — preventing the
 * tour from silently showing "no description" for a new source type.
 *
 * The matching label rendered in the popover is read from `INBOX_SOURCES`
 * directly inside `makeRowInterceptor`, so labels live in exactly one place
 * (the inbox-sources constant).
 */
const SOURCE_DESCRIPTIONS_BY_VALUE = {
  COMPANY_EVENT:
    'News and signals about your accounts — funding rounds, leadership moves, expansion, layoffs, M&A. Surfaced from Scout and external news feeds.',
  ASSIGNMENT:
    'A new account has been routed to you (manual assignment or via your team\'s rules). Read first to confirm coverage.',
  ENRICHMENT_COMPLETE:
    'A batch of accounts has finished enrichment — firmographics, contacts, location, and AI lead scoring are now populated.',
  SCOUT_LEADS_READY:
    'Scout finished discovering net-new leads matching one of your saved searches. Review them before they age out of the queue.',
  TAM_REPORT_READY:
    'A Total Addressable Market report is ready. Use it to size a segment before committing scout cycles or campaign spend.',
  RENEWAL_REPORT_READY:
    'A platform / renewal report has finished generating — production status, signals, and recommendations for an account portfolio.',
  CRM_CLEANUP:
    'We found duplicate companies in your Scout account and merged them — some are linked to your CRM. Review the duplicates and merge or archive the matching records in your CRM to keep both systems in sync.',
  ORDER_RECEIVED:
    'A new order has been parsed from inbound mail or the order forwarder, and a record was created in your CRM.',
} satisfies Record<keyof typeof INBOX_SOURCES, string>

const DEFAULT_PROMPT_TITLE = 'What each source means'
const DEFAULT_PROMPT_BODY = 'Click any source to the left to see what fires it. Filtering is paused while you\'re learning — your inbox stays put.'

/**
 * Build the popover body for step 7 with two regions:
 *   - default prompt (shown until first click)
 *   - detail (shown after clicking a row, with name + description)
 *
 * We expose `update(label, desc)` on the returned wrapper so the click
 * handler can swap content without re-rendering or knowing internal layout.
 */
type SourceLearnBody = HTMLDivElement & { update: (label: string, desc: string) => void }

/**
 * Typed view of the dataset keys we use on source-filter rows. The body-flag
 * is now owned by the generic tour-lock primitive (see SOURCE_FILTER_LOCK
 * above). `sourceValue` is the stable enum (used for description lookup);
 * `sourceLabel` is the human-readable display string for the popover.
 */
interface RowDataset {
  sourceRowSelectedInTour?: string
  sourceLabel?: string
  sourceValue?: string
}
const asRowDataset = (el: HTMLElement): RowDataset => el.dataset as unknown as RowDataset

/** Keys that activate a button by default — Enter is universal, Space is the WAI-ARIA button activator. */
const ACTIVATION_KEYS = new Set(['Enter', ' '])

function buildSourceLearnBody(): SourceLearnBody {
  const wrapper = document.createElement('div') as SourceLearnBody
  wrapper.className = 'tour-source-learn'

  const prompt = document.createElement('p')
  prompt.className = 'tour-source-learn__prompt'
  prompt.textContent = DEFAULT_PROMPT_BODY
  wrapper.append(prompt)

  const detail = document.createElement('div')
  detail.className = 'tour-source-learn__detail'
  detail.hidden = true
  const detailLabel = document.createElement('div')
  detailLabel.className = 'tour-source-learn__detail-label'
  const detailBody = document.createElement('p')
  detailBody.className = 'tour-source-learn__detail-body'
  detail.append(detailLabel, detailBody)
  wrapper.append(detail)

  wrapper.update = (label, desc) => {
    detailLabel.textContent = label
    detailBody.textContent = desc
    detail.hidden = false
    prompt.hidden = true
  }
  return wrapper
}

/**
 * Tracks the body wrapper, event handlers, and pending timers so teardown
 * can clean up regardless of which exit path (hide / cancel / complete)
 * fires. Timers are stored so an early teardown (e.g. mid-tour route change)
 * can cancel them before they fire stale interceptor attachments.
 */
const tourMode: {
  body: SourceLearnBody | undefined
  rowHandlers: Array<{ row: HTMLElement, handler: (e: Event) => void }>
  timers: Array<ReturnType<typeof setTimeout>>
} = { body: undefined, rowHandlers: [], timers: [] }

function buildSourceLearnBodyAndStash(): HTMLElement {
  tourMode.body = buildSourceLearnBody()
  return tourMode.body
}

type SourceValueKey = keyof typeof SOURCE_DESCRIPTIONS_BY_VALUE

function makeRowInterceptor(row: HTMLElement): (e: Event) => void {
  return (e: Event) => {
    // For keydown, only intercept activation keys (Enter / Space). Tab,
    // arrows, and other navigation keys must pass through unmodified so
    // keyboard users can still move focus around the dropdown.
    if (e.type === 'keydown' && !ACTIVATION_KEYS.has((e as KeyboardEvent).key)) {
      return
    }
    e.preventDefault()
    e.stopPropagation()
    e.stopImmediatePropagation()
    const value = asRowDataset(row).sourceValue ?? ''
    const label = INBOX_SOURCES[value]?.label ?? asRowDataset(row).sourceLabel ?? ''
    const desc = SOURCE_DESCRIPTIONS_BY_VALUE[value as SourceValueKey] ?? ''
    if (tourMode.body) {
      tourMode.body.update(label, desc)
    }
    const allRows = document.querySelectorAll<HTMLElement>('[data-source-row="true"]')
    for (const r of allRows) {
      asRowDataset(r).sourceRowSelectedInTour = r === row ? 'true' : undefined
    }
  }
}

function attachRowInterceptors(): void {
  const rows = document.querySelectorAll<HTMLElement>('[data-source-row="true"]')
  for (const row of rows) {
    const handler = makeRowInterceptor(row)
    row.addEventListener('pointerdown', handler, true)
    row.addEventListener('click', handler, true)
    // Keyboard activation: without this, Tab+Enter on a row fires the React
    // onClick directly, applying the filter and contradicting the tour's
    // "filtering is paused while you're learning" promise.
    row.addEventListener('keydown', handler, true)
    tourMode.rowHandlers.push({ row, handler })
  }
}

/**
 * Acquire the source-filter lock, open the Source dropdown, and wire up
 * per-row click interceptors. The lock is read by SourceFilterPill's
 * `onOpenChange` so Radix can't close the popover while the tour is active —
 * which is what keeps the dropdown stable across multiple row clicks (no
 * flicker, no re-mount).
 *
 * Timers are stashed on `tourMode.timers` so teardown can cancel them if the
 * tour exits before they fire (route change, sign-out, fast Next click).
 */
function openSourceDropdownAndArmInterceptor(): void {
  acquireTourLock(SOURCE_FILTER_LOCK)
  // Wait one paint for Shepherd's modal dialog to fully open and the
  // attached-to element to become interactive again, then click the trigger
  // to open the Source popover. A second short wait gives Radix time to
  // mount PopoverContent before we attach row interceptors.
  const t1 = globalThis.setTimeout(() => {
    const trigger = document.querySelector<HTMLButtonElement>('[data-tour="inbox-source-filter"]')
    if (!trigger || trigger.getAttribute('aria-expanded') === 'true') {
      attachRowInterceptors()
      return
    }
    trigger.click()
    const t2 = globalThis.setTimeout(attachRowInterceptors, 80)
    tourMode.timers.push(t2)
  }, 350)
  tourMode.timers.push(t1)
}

function teardownSourceLearnMode(): void {
  // Idempotent: if we don't hold the lock, this step never opened anything,
  // so there is nothing to tear down. Lets us safely call from `hide`,
  // `cancel`, and `complete` without double-toggling unrelated UI.
  if (!isTourLocked(SOURCE_FILTER_LOCK)) {
    return
  }
  // try/finally guarantees the lock is released and `tourMode.body` is
  // cleared even if a listener removal or DOM query throws — otherwise a
  // single bad row reference would strand the body flag and break the
  // source filter for the rest of the session.
  try {
    for (const t of tourMode.timers) {
      clearTimeout(t)
    }
    tourMode.timers = []
    for (const { row, handler } of tourMode.rowHandlers) {
      row.removeEventListener('pointerdown', handler, true)
      row.removeEventListener('click', handler, true)
      row.removeEventListener('keydown', handler, true)
    }
    tourMode.rowHandlers = []
    for (const r of document.querySelectorAll<HTMLElement>('[data-source-row="true"]')) {
      asRowDataset(r).sourceRowSelectedInTour = undefined
    }
  }
  finally {
    releaseTourLock(SOURCE_FILTER_LOCK)
    tourMode.body = undefined
  }
  // Close the source dropdown AFTER the lock is released. The page's
  // SourceFilterPill.handleOpenChange short-circuits close attempts while
  // the lock is held, so closing inside the try block would no-op and
  // strand the dropdown open across step transitions.
  const trigger = document.querySelector<HTMLButtonElement>('[data-tour="inbox-source-filter"]')
  if (trigger && trigger.getAttribute('aria-expanded') === 'true') {
    trigger.click()
  }
}

export const inboxIntroTour: TourDefinition = {
  id: 'inbox-intro',
  displayName: 'Inbox intro',
  featureFlag: 'tour-inbox-intro',
  trigger: 'auto',
  targetPath: '/inbox',
  steps: [
    {
      id: 'welcome',
      title: 'Welcome to your inbox',
      text:
        'This is where you\'ll find actionable insights about your leads, accounts assigned to you, TAM reports, platform reports, and much more — everything that needs your attention, in one queue.',
      buttons: [gotIt],
    },
    {
      id: 'list-panel',
      title: 'Your notifications',
      text: 'Each row is something we think you should look at. Click any item to open it and see what\'s inside.',
      attachTo: { element: '[data-tour="inbox-list-panel"]', on: 'right' },
      // +24 on x: push popover further right of the list-panel so it doesn't
      // touch the spotlight ring. Mirror of detail-panel's -24 below.
      floatingUIOptions: popoverOffset(24, 0),
      buttons: [back, next],
    },
    {
      id: 'detail-panel',
      title: 'Item details',
      text: 'When you select an inbox item, its full details land here — context, signals, and any actions you can take next.',
      attachTo: { element: '[data-tour="inbox-detail-panel"]', on: 'left' },
      // -24 on x: push popover further left of the detail-panel. Symmetric
      // with list-panel above so neither popover crowds its anchor's outline.
      floatingUIOptions: popoverOffset(-24, 0),
      buttons: [back, next],
    },
    {
      id: 'filter-bar',
      title: 'Filters',
      text: 'Use these filters to narrow your inbox to exactly what you need — by status, source, or read state.',
      attachTo: { element: '[data-tour="inbox-filter-bar"]', on: 'bottom' },
      floatingUIOptions: popoverOffset(0, 12),
      buttons: [back, next],
    },
    {
      id: 'status-filter',
      title: 'To Do vs. Done',
      text: 'Switch between items still on your plate and ones you\'ve marked as done. Marking things done lets the rest of your org know what you\'ve already covered.',
      attachTo: { element: '[data-tour="inbox-status-filter"]', on: 'bottom' },
      floatingUIOptions: popoverOffset(0, 12),
      buttons: [back, next],
    },
    {
      id: 'source-filter',
      title: 'Filter by source',
      text: 'Each notification has a source — events, enrichment results, Scout finds, TAM reports, and more. Use this to focus on one type at a time.',
      attachTo: { element: '[data-tour="inbox-source-filter"]', on: 'bottom' },
      floatingUIOptions: popoverOffset(0, 12),
      buttons: [back, next],
    },
    {
      id: 'source-types',
      title: DEFAULT_PROMPT_TITLE,
      text: buildSourceLearnBodyAndStash,
      // Anchor to the Source trigger button itself so the spotlight cutout
      // surrounds JUST the button — not the whole list panel. The Radix
      // dropdown that opens below the button is portaled to document.body
      // and lifted above the modal overlay via the z-index rule in
      // styles.css (body[data-tour-lock-source-filter='true']
      // [data-radix-popper-content-wrapper]), so it remains visible &
      // clickable even though it sits "outside" the spotlight cutout.
      attachTo: { element: '[data-tour="inbox-source-filter"]', on: 'right' },
      // x +140: push the popover past the open Radix dropdown (which
      // extends ~192px right of the button). y +28: keep the popover from
      // touching the viewport top — the source button is near the top of
      // the page and the popover would otherwise be flush against y=0.
      floatingUIOptions: popoverOffset(140, 28),
      when: {
        show(this: Step) {
          // attachInteractiveStep binds teardown to hide, cancel, AND complete
          // — Shepherd does NOT fire `hide` when the tour is cancelled or
          // completed from the active step, so without the helper's
          // three-way binding the dropdown + glow ring + body flag would
          // strand if the user dismisses mid-step. teardown is idempotent.
          attachInteractiveStep(this, {
            setup: openSourceDropdownAndArmInterceptor,
            teardown: teardownSourceLearnMode,
          })
        },
      },
      buttons: [back, next],
    },
    {
      id: 'unread-filter',
      title: 'Unread',
      text: 'Filter to items you haven\'t opened yet — useful when you only want to triage what\'s genuinely new since last visit.',
      attachTo: { element: '[data-tour="inbox-unread-filter"]', on: 'bottom' },
      floatingUIOptions: popoverOffset(0, 12),
      buttons: [back, next],
    },
    {
      id: 'refresh',
      title: 'Refresh',
      text: 'Force-pull the latest inbox state — useful when you\'re waiting on something specific. We auto-refresh every 5 minutes regardless, so you don\'t have to babysit this button.',
      attachTo: { element: '[data-tour="inbox-refresh"]', on: 'left' },
      // x -32: pull the popover further left so it sits clearer of the
      // refresh button. y +24: the refresh button is short and near the
      // viewport top, so FloatingUI's vertical-center placement squashes
      // the popover top against y=0 — push it down to a comfortable gap.
      floatingUIOptions: popoverOffset(-32, 24),
      buttons: [back, finish],
    },
  ],
}
